'use client';

import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import Image from "@/components/Image";
import { useReward } from "@/constant/reward";
import useAuthStore from "@/store/useAuthStore";
import useRewardStore from "@/store/useRewardStore";
import { usePathname, useRouter, useSearchParams } from "next/navigation";

interface DiamondPopupProps {
  visible: boolean;
  onClose: () => void;
}

const DiamondPopup = ({ visible, onClose }: DiamondPopupProps) => {
  const reward = useReward();
  const user = useAuthStore(state => state.user);
  const demoRewardMode = useRewardStore(state => state.demoRewardMode);
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  // Pre-calculate these values to avoid calling hooks in render conditionals or callbacks
  const diamondCount = reward.diamond;
  const isAnonymous = !user;
  const isDemo = isAnonymous || demoRewardMode;

  const handleGuestSignIn = () => {
    const current = searchParams.toString();
    const redirectTo = current ? `${pathname}?${current}` : pathname;
    onClose();
    router.push(`/?login=1&redirect=${encodeURIComponent(redirectTo)}`);
  };

  if (!visible) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[1200] flex items-center justify-center bg-[rgba(15,23,42,0.55)] p-4"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-[560px] rounded-[28px] border border-[#E8DDCF] bg-[#F8F5EE] px-6 pb-6 pt-7 shadow-[0_26px_80px_rgba(15,23,42,0.24)] sm:px-8 sm:pb-8 sm:pt-8"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="absolute -top-[29px] left-1/2 flex h-[61px] w-[61px] -translate-x-1/2 items-center justify-center rounded-full bg-white shadow-[0_10px_24px_rgba(15,23,42,0.12)]">
          <Image src="/assets/landing-logo.png" alt="pengi" width={49} height={49} />
        </div>
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full text-[#4B5563] transition-colors hover:bg-[#EEE9DF] hover:text-[#111827]"
          aria-label="Close gems modal"
        >
          <X className="h-4.5 w-4.5" />
        </button>

        <div className="flex flex-col items-center gap-2">
          <span className="display-text-bold mt-1 text-center text-2xl leading-tight text-[#111111] sm:text-[1.85rem]">
          {isDemo ? 'Demo Gems' : 'Gems Are Awesome!'}
          </span>
          <div className="mt-2 flex flex-col items-start gap-2 self-start">
            <div className="flex flex-row items-center">
            <Image src="/assets/diamond-3.png" alt="coin" width={24} height={24} />
              <span className="display-text ml-2 text-base font-bold text-black">
              {isDemo ? `${diamondCount} gems` : `Your Gems: ${diamondCount}`}
            </span>
            </div>
          </div>
          {isDemo && (
            <span className="display-text w-full text-start text-sm font-bold text-[#6B7280]">
              {isAnonymous
                ? 'Demo gems grow locally while you explore. Sign in to save them to your account.'
                : 'These demo gems are only for preview mode and will not affect your real account.'}
            </span>
          )}
          <span className="display-text-bold w-full text-start text-base font-bold text-black">How to get gems:</span>
          <span className="display-text w-full text-start text-base font-bold text-black">✔️ Do learn/practice sessions<br/>✔️ Finish tasks<br/>✔️ Watch Extracurricular videos</span>
          <span className="display-text-bold w-full text-start text-base font-bold text-black">How to use gems:</span>
          <span className="display-text w-full text-start text-base font-bold text-black">❤️ Buy hearts<br />🎮 Get more play time<br />🐧 Dress up Pengi with fun outfits!</span>

          <div className="mt-4 flex w-full flex-col gap-3">
            {isAnonymous ? (
              <>
                <button
                  type="button"
                  onClick={handleGuestSignIn}
                  className="display-text-semi w-full rounded-xl bg-[#42C62C] py-3 text-sm text-white transition-colors hover:bg-[#38A824]"
                >
                  Sign in to keep gems
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="display-text-semi w-full rounded-xl bg-[#ECEEF2] py-3 text-sm text-[#2F3A4A] transition-colors hover:bg-[#E2E6EC]"
                >
                  Keep exploring
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={onClose}
                className="display-text-semi w-full rounded-xl bg-[#42C62C] py-3 text-sm text-white transition-colors hover:bg-[#38A824]"
              >
                Close
              </button>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default DiamondPopup; 
